<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { inject, type Ref } from 'vue';
import PrevNextBtnBar from '@/components/PrevNextBtnBar.vue';
import FileUpload from '@/components/FileUpload.vue';
import { useDocumentConfig } from '@/models/Document';
import { hasPpaReachedSubmitRoute } from '@/helpers/tools';

// injected from PlanungsgeschaeftEditView
const saving = inject('saving') as Ref<boolean>;
const readonly = inject('readonly') as Ref<boolean>;

const documentConfig = useDocumentConfig();

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const forceValidation = hasPpaReachedSubmitRoute(props.geschaeft);

const emit = defineEmits<{
    next: [];
    prev: [];
}>();

function onReset() {
    emit('prev');
}

async function onNextStep() {
    emit('next');
}
</script>

<template>
    <div>
        <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('weitere_unterlagen') }}</h4>
        <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-3_5rem px-6rem">
            <h5 class="ktg-title-05 ktg-inner-box-padding">{{ $t('weitere_unterlagen') }}</h5>
            <ktg-line class="mt-0_5rem mb-1rem"></ktg-line>


            <FileUpload
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="vertraege"
                :title="$t('vertraege') + (documentConfig?.planungsgeschaeft?.vertraege?.min > 0 ? ' *' : '')"
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.vertraege?.min"
                :max="documentConfig?.planungsgeschaeft?.vertraege?.max"
                :display-min="documentConfig?.planungsgeschaeft?.vertraege?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_vertraege')"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="tabelle"
                :title="
                    $t('tabellen') +
                    (documentConfig?.planungsgeschaeft?.tabelle?.min > 0 ? ' *' : '')
                "
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.tabelle?.min"
                :max="documentConfig?.planungsgeschaeft?.tabelle?.max"
                :display-min="documentConfig?.planungsgeschaeft?.tabelle?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_tabellen')"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="fotos"
                :title="$t('fotos') + (documentConfig?.planungsgeschaeft?.fotos?.min > 0 ? ' *' : '')"
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.fotos?.min"
                :max="documentConfig?.planungsgeschaeft?.fotos?.max"
                :display-min="documentConfig?.planungsgeschaeft?.fotos?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="weitere_unterlagen"
                :title="
                    $t('weitere_unterlagen') +
                    (documentConfig?.planungsgeschaeft?.weitere_unterlagen?.min > 0 ? ' *' : '')
                "
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.weitere_unterlagen?.min"
                :max="documentConfig?.planungsgeschaeft?.weitere_unterlagen?.max"
                :display-min="documentConfig?.planungsgeschaeft?.weitere_unterlagen?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
            ></FileUpload>

            <ktg-line class="mt-2rem mb-1rem"></ktg-line>
            <PrevNextBtnBar
                :back-text="$t('back')"
                :next-text="$t('to_next_step')"
                :disabled="saving"
                @next="onNextStep"
                @back="onReset"
            />
        </fieldset>
    </div>
</template>
